import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Modal, Button, Form, Row, Col, Card, Alert, Spinner } from "react-bootstrap";
import { fetchTicketOptions, createBookingEnquiry } from "../services/TicketService";
import { getLoggedInUser } from "../services/UserService";
import TicketOptionData from "../models/TicketOptionData";
import { BookingTicket } from "../models/BookingEnquiryData";
import axiosInstance from "../services/BaseService";
import { RootState } from "../states/rootReducer";
import { validateEmail, validatePhone, validateRequired } from "../utils/validation";

interface BookingModalProps {
  show: boolean;
  onHide: () => void;
  schedule: any;
  disableAutoPopulate?: boolean;
}

const BookingModal: React.FC<BookingModalProps> = ({ show, onHide, schedule, disableAutoPopulate = false }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [ticketOptions, setTicketOptions] = useState<TicketOptionData[]>([]);
  const [selectedTickets, setSelectedTickets] = useState<BookingTicket[]>([]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [bookingResult, setBookingResult] = useState<any>(null);
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes in seconds
  const [transactionId, setTransactionId] = useState("");
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [actionCompleted, setActionCompleted] = useState<'done' | 'later' | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [captchaNum1, setCaptchaNum1] = useState(0);
  const [captchaNum2, setCaptchaNum2] = useState(0);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaError, setCaptchaError] = useState(false);

  useEffect(() => {
    if (show && schedule?._id) {
      loadTicketOptions();
      generateCaptcha();
      if (isAuthenticated && !disableAutoPopulate) {
        fetchUserDetails();
      }
    }
  }, [show, schedule, isAuthenticated, disableAutoPopulate]);

  const generateCaptcha = () => {
    setCaptchaNum1(Math.floor(Math.random() * 10) + 1);
    setCaptchaNum2(Math.floor(Math.random() * 10) + 1);
    setCaptchaAnswer("");
    setCaptchaError(false);
  };

  const fetchUserDetails = async () => {
    try {
      const user = await getLoggedInUser();
      setUserData(user);
      
      const fullName = user.firstName && user.lastName 
        ? `${user.firstName} ${user.lastName}` 
        : user.username || '';
      
      setCustomerName(fullName);
      setPhoneNumber(user.phone || '');
      setEmail(user.email || '');
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const loadTicketOptions = async () => {
    try {
      setLoading(true);
      const options = await fetchTicketOptions(schedule._id);
      setTicketOptions(options);
      // Initialize selected tickets
      setSelectedTickets(options.map(option => ({
        ticketOptionId: option._id!,
        quantity: 0
      })));
    } catch (err) {
      setError("Failed to load ticket options");
    } finally {
      setLoading(false);
    }
  };

  const updateTicketQuantity = (ticketOptionId: string, quantity: number) => {
    setSelectedTickets(prev => 
      prev.map(ticket => 
        ticket.ticketOptionId === ticketOptionId 
          ? { ...ticket, quantity: Math.max(0, quantity) }
          : ticket
      )
    );
  };

  const getTotalAmount = () => {
    return selectedTickets.reduce((total, ticket) => {
      const option = ticketOptions.find(opt => opt._id === ticket.ticketOptionId);
      return total + (option ? option.price * ticket.quantity : 0);
    }, 0);
  };

  const getTotalTickets = () => {
    return selectedTickets.reduce((total, ticket) => total + ticket.quantity, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const nameValidation = validateRequired(customerName, "Name");
    if (!nameValidation.isValid) {
      setError(nameValidation.message!);
      return;
    }

    if (customerName.trim().length > 20) {
      setError("Name should not exceed 20 characters");
      return;
    }

    const phoneValidation = validatePhone(phoneNumber);
    if (!phoneValidation.isValid) {
      setError(phoneValidation.message!);
      return;
    }

    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.message!);
      return;
    }

    if (getTotalTickets() === 0) {
      setError("Please select at least one ticket");
      return;
    }

    // Validate CAPTCHA
    const expectedAnswer = captchaNum1 + captchaNum2;
    if (parseInt(captchaAnswer) !== expectedAnswer) {
      setCaptchaError(true);
      setError("Please solve the CAPTCHA correctly");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const validTickets = selectedTickets.filter(ticket => ticket.quantity > 0);
      
      const bookingData = {
        scheduleId: schedule._id,
        phoneNumber: phoneNumber.trim(),
        customerName: customerName.trim(),
        email: email.trim(),
        tickets: validTickets
      };

      const result = await createBookingEnquiry(bookingData);
      setBookingResult(result);
      setSuccess(true);
      setTimeLeft(180); // Reset timer to 3 minutes
      
      // Trigger push notification to group owner
      try {
        await axiosInstance.post('/notifications/booking-created', {
          bookingId: result._id,
          programId: schedule.programId?._id
        });
      } catch (notifError) {
        console.error('Failed to send notification:', notifError);
      }
      
    } catch (err) {
      setError("Failed to create booking enquiry");
    } finally {
      setLoading(false);
    }
  };

  const generateWhatsAppMessage = (booking: any) => {
    const playTitle = schedule.programId?.title || "Theater Show";
    const showDate = new Date(schedule.startDate).toLocaleDateString();
    const showTime = schedule.timing;
    const venue = schedule.address;
    
    let ticketDetails = "";
    selectedTickets.filter(t => t.quantity > 0).forEach(ticket => {
      const option = ticketOptions.find(opt => opt._id === ticket.ticketOptionId);
      if (option) {
        ticketDetails += `${option.ticketName}: ${ticket.quantity} tickets @ ₹${option.price} each\n`;
      }
    });

    return `🎭 *New Booking Enquiry*

Hello ${schedule.organizerName || 'Organizer'},

I would like to book tickets for:

*Show:* ${playTitle}
*Date:* ${showDate}
*Time:* ${showTime}
*Venue:* ${venue}

*Tickets Requested:*
${ticketDetails}
*Total Amount:* ₹${getTotalAmount()}
*My Phone:* ${phoneNumber}

Please confirm the availability and booking process.

Thank you!`;
  };

  const resetForm = () => {
    setPhoneNumber("");
    setCustomerName("");
    setEmail("");
    setSelectedTickets([]);
    setError(null);
    setSuccess(false);
    setBookingResult(null);
    setTimeLeft(180);
    setTransactionId("");
    setPaymentProcessing(false);
    setActionCompleted(null);
    setUserData(null);
    setCaptchaAnswer("");
    setCaptchaError(false);
    generateCaptcha();
  };

  const calculatePaymentDeadline = () => {
    const scheduleDate = new Date(schedule.startDate);
    const now = new Date();
    const timeDiff = scheduleDate.getTime() - now.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    const hoursDiff = Math.ceil(timeDiff / (1000 * 3600));
    
    if (daysDiff >= 7) {
      return { hours: 24, message: "24 hours" };
    } else if (daysDiff >= 1) {
      return { hours: 6, message: "6 hours" };
    } else {
      return { hours: 1, message: "60 minutes" };
    }
  };

  const generatePaymentDoneMessage = () => {
    const playTitle = schedule.programId?.title || "Theater Show";
    const showDate = new Date(schedule.startDate).toLocaleDateString();
    const showTime = schedule.timing;
    const venue = schedule.address;
    const bookingCode = bookingResult?.bookingCode || "";
    
    let ticketDetails = "";
    selectedTickets.filter(t => t.quantity > 0).forEach(ticket => {
      const option = ticketOptions.find(opt => opt._id === ticket.ticketOptionId);
      if (option) {
        ticketDetails += `${option.ticketName}: ${ticket.quantity} tickets @ ₹${option.price} each\n`;
      }
    });

    return `🎭 *Payment Completed*

Hello ${schedule.organizerName || 'Organizer'},

Payment completed for booking:

*Booking Code:* ${bookingCode}
*Transaction ID:* ${transactionId || 'Not provided'}
*Customer Name:* ${customerName}
*Phone Number:* ${phoneNumber}

*Show Details:*
*Program:* ${playTitle}
*Date:* ${showDate}
*Time:* ${showTime}
*Venue:* ${venue}

*Ticket Details:*
${ticketDetails}
*Total Amount:* ₹${getTotalAmount()}

Please confirm the booking.

Thank you!`;
  };

  const generatePayLaterMessage = () => {
    const playTitle = schedule.programId?.title || "Theater Show";
    const showDate = new Date(schedule.startDate).toLocaleDateString();
    const showTime = schedule.timing;
    const venue = schedule.address;
    const bookingCode = bookingResult?.bookingCode || "";
    const deadline = calculatePaymentDeadline();
    
    let ticketDetails = "";
    selectedTickets.filter(t => t.quantity > 0).forEach(ticket => {
      const option = ticketOptions.find(opt => opt._id === ticket.ticketOptionId);
      if (option) {
        ticketDetails += `${option.ticketName}: ${ticket.quantity} tickets @ ₹${option.price} each\n`;
      }
    });

    return `🎭 *Booking - Payment Pending*

Hello ${schedule.organizerName || 'Organizer'},

New booking with pending payment:

*Booking Code:* ${bookingCode}
*Customer Name:* ${customerName}
*Phone Number:* ${phoneNumber}

*Show Details:*
*Program:* ${playTitle}
*Date:* ${showDate}
*Time:* ${showTime}
*Venue:* ${venue}

*Ticket Details:*
${ticketDetails}
*Pending Amount:* ₹${getTotalAmount()}

⚠️ *Payment deadline:* ${deadline.message} from booking time

Customer will complete payment soon.

Thank you!`;
  };

  const handlePaymentAction = async (action: 'done' | 'later') => {
    if (!bookingResult?._id) return;
    
    // Validate transaction ID for "Payment Done" action
    if (action === 'done' && !transactionId.trim()) {
      setError("Transaction ID is required for payment confirmation");
      return;
    }
    
    try {
      setPaymentProcessing(true);
      const status = action === 'done' ? 'verification-pending' : 'pending';
      
      // Update booking status via API using axiosInstance
      await axiosInstance.patch(`/tickets/booking-enquiry/${bookingResult._id}/status`, { 
        status,
        transactionId: action === 'done' ? transactionId : undefined
      });
      
      // Set action completed to show appropriate message (no automatic WhatsApp redirect)
      setActionCompleted(action);
    } catch (error) {
      setError('Failed to update payment status');
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Timer effect - continue showing payment actions after timer expires
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (success && timeLeft > 0) {
      timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [success, timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const generateUPIQRData = () => {
    if (!bookingResult?.bookingCode) return "";
    const amount = getTotalAmount();
    const upiId = schedule?.upiId || "organizer@upi";
    const upiName = schedule?.upiName || "Organizer";
    const note = `${bookingResult.bookingCode} - ${schedule?.programId?.title}`;
    
    // Generate UPI payment deep link for QR code
    return `upi://pay?pa=${upiId}&pn=${encodeURIComponent(upiName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(note)}`;
  };

  const generateUPILink = (app: 'gpay' | 'phonepe') => {
    if (!bookingResult?.bookingCode) return "";
    const amount = getTotalAmount();
    const upiId = schedule?.upiId || "organizer@upi";
    const upiName = schedule?.upiName || "Organizer";
    const note = `${bookingResult.bookingCode} - ${schedule?.programId?.title}`;
    
    if (app === 'gpay') {
      return `tez://upi/pay?pa=${upiId}&pn=${encodeURIComponent(upiName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(note)}`;
    } else {
      return `phonepe://pay?pa=${upiId}&pn=${encodeURIComponent(upiName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(note)}`;
    }
  };

  const SimpleQRCode = ({ data }: { data: string }) => {
    console.log('UPI QR Data:', data);
    
    if (!data) {
      return (
        <div className="d-flex align-items-center justify-content-center border" style={{ width: '150px', height: '150px', backgroundColor: '#f8f9fa' }}>
          <small className="text-danger">No QR Data</small>
        </div>
      );
    }
    
    // Generate QR code using Google Charts API
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(data)}`;
    
    return (
      <div className="text-center">
        <img 
          src={qrUrl} 
          alt="UPI Payment QR Code" 
          style={{ width: '150px', height: '150px', border: '1px solid #ddd' }}
          onError={(e) => {
            console.error('QR Code failed to load');
            e.currentTarget.style.display = 'none';
          }}
        />
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ backgroundColor: '#810081', color: 'white' }}>
        <Modal.Title>
          <i className="bi bi-ticket-perforated me-2"></i>
          Book Tickets
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="p-4">
        {success && !actionCompleted ? (
          <div className="text-center">
            <Alert variant="success" className="mb-4">
              <i className="bi bi-check-circle-fill me-2" style={{ fontSize: '2rem' }}></i>
              <h5>Booking Enquiry Submitted Successfully!</h5>
              <p className="mb-2">Complete your payment to confirm booking</p>
              <div className="badge bg-warning text-dark fs-6">
                <i className="bi bi-clock me-1"></i>
                Time remaining: {formatTime(timeLeft)}
              </div>
            </Alert>
 {/* Payment Instructions */}
            <Alert variant="info" className="mb-4">
              <div className="text-center">
                <i className="bi bi-info-circle-fill me-2" style={{ fontSize: '1.2rem' }}></i>
                <strong>Important Instructions:</strong>
                <p className="mb-0 mt-2">
                  After completing payment (QR scan, UPI payment, or bank transfer), 
                  make sure you provide the <strong>Transaction ID for Net Banking & UTR Number For UPI Payment</strong> below to confirm your booking.
                </p>
              </div>
            </Alert>
            {timeLeft > 0 && (
              <>
                {/* UPI QR Code and Payment Details */}
                <Row className="mb-4">
                  <Col md={6} className="text-center mb-3">
                    <h6 style={{ color: '#810081' }}>UPI Payment QR Code</h6>
                    <div className="d-flex justify-content-center mb-2">
                      <SimpleQRCode data={generateUPIQRData()} />
                    </div>
                    <small className="text-muted">Scan to pay with any UPI app</small>
                  </Col>
                  <Col md={6}>
                    <h6 style={{ color: '#810081' }}>Payment Details</h6>
                    <Card className="border-0" style={{ backgroundColor: 'rgba(255, 214, 0, 0.1)' }}>
                      <Card.Body>
                        <div className="mb-2">
                          <strong>Amount: ₹{getTotalAmount()}</strong>
                        </div>
                        <div className="mb-2">
                          <small><strong>UPI ID:</strong> {schedule?.upiId || 'Not available'}</small>
                        </div>
                        <div className="mb-2">
                          <small><strong>Account Holder:</strong> {schedule?.upiName || 'Not available'}</small>
                        </div>
                        {schedule?.bankName && (
                          <>
                            <hr className="my-2" />
                            <div className="mb-1">
                              <small><strong>Bank:</strong> {schedule.bankName}</small>
                            </div>
                            <div className="mb-1">
                              <small><strong>Account:</strong> {schedule.accountNumber}</small>
                            </div>
                            <div className="mb-1">
                              <small><strong>IFSC:</strong> {schedule.ifscCode}</small>
                            </div>
                            <div className="mb-1">
                              <small><strong>Name:</strong> {schedule.accountHolderName}</small>
                            </div>
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Payment Buttons */}
                <div className="mb-4">
                  <h6 style={{ color: '#810081' }}>Pay Now</h6>
                  <div className="d-flex justify-content-center gap-3 mb-3">
                    <Button
                      onClick={() => window.open(generateUPILink('gpay'), '_blank')}
                      className="d-flex align-items-center"
                      style={{ backgroundColor: '#4285f4', border: 'none' }}
                    >
                      <i className="bi bi-google me-2"></i>
                      Pay with GPay
                    </Button>
                    <Button
                      onClick={() => window.open(generateUPILink('phonepe'), '_blank')}
                      className="d-flex align-items-center"
                      style={{ backgroundColor: '#5f259f', border: 'none' }}
                    >
                      <i className="bi bi-phone me-2"></i>
                      Pay with PhonePe
                    </Button>
                  </div>
                  <div className="text-center">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      After payment, share screenshot with organizer for confirmation
                    </small>
                  </div>
                </div>
              </>
            )}

           

            {error && (
              <Alert variant="danger" className="mb-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}

            {/* Payment Action Section */}
            <Card className="border-0" style={{ backgroundColor: 'rgba(129, 0, 129, 0.05)' }}>
              <Card.Body>
                <h6 style={{ color: '#810081' }} className="mb-3">Payment Status</h6>
                
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">UTR Number / Transaction ID</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter UTR for UPI Payment or Transaction ID (required for payment confirmation)"
                        value={transactionId}
                        onChange={(e) => setTransactionId(e.target.value)}
                        className="py-2"
                        style={{ borderRadius: '10px' }}
                      />
                      <Form.Text className="text-muted">
                        Required only when clicking "Payment Done"
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                
                <div className="d-flex justify-content-center gap-3">
                  <Button
                    onClick={() => handlePaymentAction('done')}
                    disabled={paymentProcessing}
                    className="px-4"
                    style={{ backgroundColor: '#28a745', border: 'none' }}
                  >
                    {paymentProcessing ? (
                      <Spinner size="sm" className="me-2" />
                    ) : (
                      <i className="bi bi-check-circle me-2"></i>
                    )}
                    Payment Done
                  </Button>
                  <Button
                    onClick={() => handlePaymentAction('later')}
                    disabled={paymentProcessing}
                    variant="outline-secondary"
                    className="px-4"
                  >
                    {paymentProcessing ? (
                      <Spinner size="sm" className="me-2" />
                    ) : (
                      <i className="bi bi-clock me-2"></i>
                    )}
                    Pay Later
                  </Button>
                </div>
                
                <div className="text-center mt-3">
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Payment Done: Status will be "Verification Pending"<br />
                    Pay Later: You can complete payment anytime
                  </small>
                </div>
              </Card.Body>
            </Card>
          </div>
        ) : success && actionCompleted ? (
          <div className="text-center">
            {actionCompleted === 'done' ? (
              <Alert variant="success" className="mb-4">
                <i className="bi bi-check-circle-fill me-2" style={{ fontSize: '2rem', color: '#28a745' }}></i>
                <h5>Thank You for Payment!</h5>
                <p className="mb-2">Your payment has been recorded successfully.</p>
                <p className="mb-0">Booking will be confirmed by organizer soon and ticket details will be shared on your provided mobile number: <strong>{phoneNumber}</strong> and email: <strong>{email}</strong></p>
                <p className="mb-0"><strong>Didn’t receive the email yet?</strong> It may have reached your Spam/Promotions folder.
Kindly check and mark the email as “Not Spam” for smooth communication.</p>
              </Alert>
            ) : (
              <Alert variant="warning" className="mb-4">
                <i className="bi bi-clock-fill me-2" style={{ fontSize: '2rem', color: '#ffc107' }}></i>
                <h5>Payment Pending</h5>
                <p className="mb-2">Your booking has been saved with pending payment status.</p>
                <div className="alert alert-danger mt-3">
                  <strong>Important:</strong> Your booking will be automatically cancelled if payment is not completed within <strong>{calculatePaymentDeadline().message}</strong> from now.
                </div>
                <p className="mb-0">Please complete the payment as soon as possible to secure your booking.</p>
              </Alert>
            )}
            
            {/* Contact Organizer Section */}
            <Card className="mb-4 border-0" style={{ backgroundColor: 'rgba(129, 0, 129, 0.05)' }}>
              <Card.Body>
                <h6 style={{ color: '#810081' }} className="mb-3">
                  <i className="bi bi-person-lines-fill me-2"></i>
                  Contact Organizer
                </h6>
                
                <div className="d-flex flex-column gap-3">
                  {/* WhatsApp Button */}
                  {schedule?.organizerPhone && (
                    <Button
                      onClick={() => {
                        const organizerPhone = schedule.organizerPhone?.replace(/[^0-9]/g, '');
                        const whatsappMessage = actionCompleted === 'done' 
                          ? generatePaymentDoneMessage() 
                          : generatePayLaterMessage();
                        const whatsappUrl = `https://wa.me/${organizerPhone}?text=${encodeURIComponent(whatsappMessage)}`;
                        window.open(whatsappUrl, '_blank');
                      }}
                      className="d-flex align-items-center justify-content-center"
                      style={{ backgroundColor: '#25D366', border: 'none' }}
                    >
                      <i className="bi bi-whatsapp me-2"></i>
                      Message on WhatsApp
                    </Button>
                  )}
                  
                  {/* Contact Details */}
                  <div className="d-flex flex-column gap-2">
                    {schedule?.organizerPhone && (
                      <div className="d-flex align-items-center justify-content-center">
                        <i className="bi bi-telephone-fill me-2" style={{ color: '#810081' }}></i>
                        <span>{schedule.organizerPhone}</span>
                      </div>
                    )}
                    {schedule?.organizerEmail && (
                      <div className="d-flex align-items-center justify-content-center">
                        <i className="bi bi-envelope-fill me-2" style={{ color: '#810081' }}></i>
                        <span>{schedule.organizerEmail}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
            
            <Button
              onClick={() => {
                onHide();
                resetForm();
              }}
              className="px-4"
              style={{ backgroundColor: '#810081', border: 'none' }}
            >
              <i className="bi bi-check me-2"></i>
              Close
            </Button>
          </div>
        ) : (
          <>
            {/* Show Details */}
            <Card className="mb-4 border-0" style={{ backgroundColor: 'rgba(129, 0, 129, 0.1)' }}>
              <Card.Body>
                <h6 style={{ color: '#810081' }}>{schedule?.programId?.title}</h6>
                <div className="d-flex flex-wrap gap-3 mt-2">
                  <small>
                    <i className="bi bi-calendar me-1"></i>
                    {new Date(schedule?.startDate).toLocaleDateString()}
                    {schedule?.endDate && schedule.endDate !== schedule.startDate && (
                      <span> - {new Date(schedule.endDate).toLocaleDateString()}</span>
                    )}
                  </small>
                  <small><i className="bi bi-clock me-1"></i>{schedule?.timing}</small>
                  <small><i className="bi bi-geo-alt me-1"></i>{schedule?.address}</small>
                </div>
                {schedule?.organizerName && (
                  <div className="mt-2">
                    <small className="text-muted">
                      <i className="bi bi-person me-1"></i>Organizer: {schedule.organizerName}
                      {schedule.organizerPhone && (
                        <span className="ms-2">
                          <i className="bi bi-telephone me-1"></i>{schedule.organizerPhone}
                        </span>
                      )}
                    </small>
                  </div>
                )}
              </Card.Body>
            </Card>

            <Form onSubmit={handleSubmit}>
              {/* Customer Details */}
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold" style={{ color: '#810081' }}>
                      Full Name *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your full name"
                      value={customerName}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 20) {
                          setCustomerName(value);
                        }
                      }}
                      required
                      maxLength={20}
                      readOnly={isAuthenticated && userData && !disableAutoPopulate}
                      className={`py-2 ${isAuthenticated && userData && !disableAutoPopulate ? 'bg-light' : ''}`}
                      style={{ borderRadius: '10px', borderColor: '#FFD600' }}
                    />
                    <Form.Text className="text-muted">
                      {customerName.length}/20 characters
                    </Form.Text>
                    {isAuthenticated && userData && !disableAutoPopulate && (
                      <Form.Text className="text-muted">
                        <i className="bi bi-check-circle text-success me-1"></i>
                        Auto-filled from profile
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold" style={{ color: '#810081' }}>
                      Email *
                    </Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      readOnly={isAuthenticated && userData && !disableAutoPopulate}
                      className={`py-2 ${isAuthenticated && userData && !disableAutoPopulate ? 'bg-light' : ''}`}
                      style={{ borderRadius: '10px', borderColor: '#FFD600' }}
                      isInvalid={!!email && !validateEmail(email).isValid}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter a valid email address
                    </Form.Control.Feedback>
                    {isAuthenticated && userData && !disableAutoPopulate && (
                      <Form.Text className="text-muted">
                        <i className="bi bi-check-circle text-success me-1"></i>
                        Auto-filled from profile
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold" style={{ color: '#810081' }}>
                      Phone Number *
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Enter your 10-digit mobile number"
                      value={phoneNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value.length <= 10) {
                          setPhoneNumber(value);
                        }
                      }}
                      required
                      maxLength={10}
                      readOnly={isAuthenticated && userData && !disableAutoPopulate}
                      className={`py-2 ${isAuthenticated && userData && !disableAutoPopulate ? 'bg-light' : ''}`}
                      style={{ borderRadius: '10px', borderColor: '#FFD600' }}
                      isInvalid={!!phoneNumber && !validatePhone(phoneNumber).isValid}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter a valid 10-digit Indian mobile number (starting with 6-9)
                    </Form.Control.Feedback>
                    {isAuthenticated && userData && !disableAutoPopulate && (
                      <Form.Text className="text-muted">
                        <i className="bi bi-check-circle text-success me-1"></i>
                        Auto-filled from profile
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Ticket Options */}
              <h6 className="mb-3" style={{ color: '#810081' }}>Select Tickets</h6>
              {loading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" style={{ color: '#810081' }} />
                </div>
              ) : (
                <Row className="g-3 mb-4">
                  {ticketOptions.map((option) => (
                    <Col md={6} key={option._id}>
                      <Card className="h-100 border-2" style={{ borderColor: '#FFD600' }}>
                        <Card.Body className="text-center">
                          <h6 style={{ color: '#810081' }}>{option.ticketName}</h6>
                          <div className="mb-2">
                            <span className="h5" style={{ color: '#FFD600' }}>₹{option.price}</span>
                          </div>
                          <div className="mb-3">
                            <small className={`badge ${(option.availableSeats || 0) === 0 ? 'bg-danger' : (option.availableSeats || 0) < 10 ? 'bg-warning' : 'bg-success'}`}>
                              {(option.availableSeats || 0)} seats available
                            </small>
                          </div>
                          {(option.availableSeats || 0) > 0 ? (
                            <div className="d-flex align-items-center justify-content-center">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  const current = selectedTickets.find(t => t.ticketOptionId === option._id)?.quantity || 0;
                                  updateTicketQuantity(option._id!, current - 1);
                                }}
                                disabled={selectedTickets.find(t => t.ticketOptionId === option._id)?.quantity === 0}
                              >
                                -
                              </Button>
                              <span className="mx-3 fw-bold">
                                {selectedTickets.find(t => t.ticketOptionId === option._id)?.quantity || 0}
                              </span>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  const current = selectedTickets.find(t => t.ticketOptionId === option._id)?.quantity || 0;
                                  const maxAllowed = Math.min((option.availableSeats || 0), 10); // Limit to 10 tickets per person
                                  if (current < maxAllowed) {
                                    updateTicketQuantity(option._id!, current + 1);
                                  }
                                }}
                                disabled={(() => {
                                  const current = selectedTickets.find(t => t.ticketOptionId === option._id)?.quantity || 0;
                                  const maxAllowed = Math.min((option.availableSeats || 0), 10);
                                  return current >= maxAllowed;
                                })()}
                              >
                                +
                              </Button>
                            </div>
                          ) : (
                            <div className="text-muted">
                              <i className="bi bi-x-circle me-1"></i>
                              Sold Out
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}

              {/* Total */}
              {getTotalTickets() > 0 && (
                <Card className="mb-4 border-0" style={{ backgroundColor: 'rgba(255, 214, 0, 0.1)' }}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-semibold">Total: {getTotalTickets()} tickets</span>
                      <span className="h5 mb-0" style={{ color: '#810081' }}>₹{getTotalAmount()}</span>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* CAPTCHA */}
              <Card className="mb-4 border-0" style={{ backgroundColor: 'rgba(129, 0, 129, 0.05)' }}>
                <Card.Body>
                  <h6 style={{ color: '#810081' }} className="mb-3">Security Verification</h6>
                  <Row className="align-items-center">
                    <Col md={6}>
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold" style={{ fontSize: '1.2rem' }}>{captchaNum1} + {captchaNum2} = ?</span>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={generateCaptcha}
                          title="Refresh CAPTCHA"
                        >
                          <i className="bi bi-arrow-clockwise"></i>
                        </Button>
                      </div>
                    </Col>
                    <Col md={6}>
                      <Form.Control
                        type="number"
                        placeholder="Enter answer"
                        value={captchaAnswer}
                        onChange={(e) => {
                          setCaptchaAnswer(e.target.value);
                          setCaptchaError(false);
                        }}
                        isInvalid={captchaError}
                        style={{ borderRadius: '10px' }}
                      />
                      <Form.Control.Feedback type="invalid">
                        Incorrect answer. Please try again.
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

  
            </Form>
          </>
        )}
      </Modal.Body>
      
      {!success && (
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || getTotalTickets() === 0 || !phoneNumber.trim() || !customerName.trim() || !email.trim() || !captchaAnswer.trim()}
            className="px-4"
            style={{ backgroundColor: '#FFD600', color: '#810081', border: 'none' }}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Submitting...
              </>
            ) : (
              <>
                <i className="bi bi-send me-2"></i>
                Submit Booking Request
              </>
            )}
          </Button>
        </Modal.Footer>
      )}
      
      {success && !actionCompleted && (
        <Modal.Footer className="justify-content-center">
          <Button
            variant="outline-secondary"
            onClick={() => {
              onHide();
              resetForm();
            }}
          >
            <i className="bi bi-x-circle me-2"></i>
            Close
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default BookingModal;